/**
 * Computes needsOnboarding once per session (refreshed when wizardFormId / workspace changes).
 */

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { isAdmin } from '../utils/admin';
import { isTrialCurrentlyActive } from '../utils/trialUtils';

const OnboardingGateContext = createContext({
  needsOnboarding: false,
  onboardingGateReady: false,
  refreshOnboardingGate: () => {},
});

export function useOnboardingGate() {
  return useContext(OnboardingGateContext);
}

function isInvitedTeamMember(workspaceDetails) {
  return (workspaceDetails || []).some((d) => d.role && d.role !== 'owner');
}

export function OnboardingGateProvider({ children }) {
  const { currentUser, userProfile } = useAuth();
  const { selectedWorkspaceId, workspaceDetails, loading: workspaceLoading } = useWorkspace();
  const [hasFormsInWorkspace, setHasFormsInWorkspace] = useState(null);
  const [checkingForms, setCheckingForms] = useState(false);
  const formsCheckedForKey = useRef(null);

  const refreshOnboardingGate = () => {
    formsCheckedForKey.current = null;
    setHasFormsInWorkspace(null);
  };

  useEffect(() => {
    if (!currentUser?.uid || !selectedWorkspaceId || workspaceLoading) return;

    if (userProfile?.wizardFormId || userProfile?.onboardingCompleted) {
      setHasFormsInWorkspace(true);
      formsCheckedForKey.current = `${selectedWorkspaceId}:done`;
      return;
    }

    const checkKey = `${selectedWorkspaceId}`;
    if (formsCheckedForKey.current === checkKey) return;
    formsCheckedForKey.current = checkKey;

    let cancelled = false;
    (async () => {
      setCheckingForms(true);
      try {
        const q = query(
          collection(db, 'forms'),
          where('workspaceId', '==', selectedWorkspaceId),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!cancelled) setHasFormsInWorkspace(!snap.empty);
      } catch (e) {
        console.warn('[ONBOARDING GATE] forms check failed:', e);
        if (!cancelled) setHasFormsInWorkspace(false);
      } finally {
        if (!cancelled) setCheckingForms(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentUser?.uid,
    selectedWorkspaceId,
    workspaceLoading,
    userProfile?.wizardFormId,
    userProfile?.onboardingCompleted,
  ]);

  const needsOnboarding = useMemo(() => {
    if (!currentUser || !userProfile) return false;
    if (isAdmin(currentUser, userProfile)) return false;
    if (!isTrialCurrentlyActive(userProfile)) return false;
    if (isInvitedTeamMember(workspaceDetails)) return false;
    if (selectedWorkspaceId !== currentUser.uid) return false;
    if (userProfile.onboardingCompleted) return false;
    if (userProfile.wizardFormId) return false;
    if (hasFormsInWorkspace === true) return false;
    if (hasFormsInWorkspace === null) return false;
    return true;
  }, [
    currentUser,
    userProfile,
    workspaceDetails,
    selectedWorkspaceId,
    hasFormsInWorkspace,
  ]);

  const onboardingGateReady =
    !workspaceLoading &&
    !checkingForms &&
    (hasFormsInWorkspace !== null ||
      !!userProfile?.wizardFormId ||
      !!userProfile?.onboardingCompleted);

  const value = useMemo(
    () => ({ needsOnboarding, onboardingGateReady, refreshOnboardingGate }),
    [needsOnboarding, onboardingGateReady]
  );

  return (
    <OnboardingGateContext.Provider value={value}>
      {children}
    </OnboardingGateContext.Provider>
  );
}
